import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Helmet } from 'react-helmet-async';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import Footer from '../components/Footer';
import StepHeader from '../components/shared/StepHeader';
import { BACKEND_URL as CONFIG_BACKEND_URL } from '../config/api';

const BACKEND_URL = CONFIG_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// PayPal Configuration - LIVE MODE (payments@ecuflashservice.com)
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

// SEO Keywords and Meta Data for DTC Delete Tool
const SEO_DATA = {
  title: "DTC Delete Tool | Check Engine Light Removal | Error Code Delete Service",
  description: "Professional DTC deletion service. Remove check engine light, clear diagnostic trouble codes P0420, P0401, P2002, EGR, DPF, AdBlue codes. From $10. Instant download. Checksum correction included.",
  keywords: [
    // Primary Keywords
    "DTC delete", "DTC removal", "check engine light removal", "error code delete",
    "diagnostic trouble code removal", "fault code delete", "engine light off",
    // Service Keywords  
    "ECU DTC delete service", "permanent DTC removal", "DTC file service",
    "check engine light off service", "CEL delete", "MIL delete",
    // Code-Specific Keywords
    "P0420 delete", "P0401 remove", "P2002 delete", "P0171 remove",
    "EGR code delete", "DPF code removal", "AdBlue code delete", "SCR code removal",
    "catalyst code delete", "O2 sensor code removal", "lambda code delete",
    // Vehicle Keywords
    "diesel DTC delete", "truck error code removal", "car fault code delete",
    "BMW DTC delete", "Audi fault code removal", "Mercedes error code delete",
    "Ford DTC removal", "Toyota fault code delete", "VW error code service",
    // Technical Keywords
    "ECU fault code removal", "ECM error code delete", "OBD2 code removal",
    "checksum correction", "ECU flash DTC delete", "ECU tuning DTC removal",
    // Location/Service Keywords
    "online DTC delete service", "remote DTC removal", "instant DTC delete",
    "cheap DTC delete", "affordable error code removal", "fast DTC service",
    // Problem-Specific Keywords
    "how to remove check engine light", "clear engine codes permanently",
    "delete fault codes without scanner", "remove DTC from ECU file",
    "fix check engine light", "stop engine warning light"
  ].join(", "),
  canonical: "https://ecuflashservice.com/tools/dtc-delete",
  ogImage: "/dtc-delete-og.jpg"
};

// DTC Descriptions for common codes
const DTC_DESCRIPTIONS = {
  // Catalyst
  'P0420': 'Catalyst System Efficiency Below Threshold (Bank 1)',
  'P0421': 'Warm Up Catalyst Efficiency Below Threshold (Bank 1)',
  'P0430': 'Catalyst System Efficiency Below Threshold (Bank 2)',
  'P0431': 'Warm Up Catalyst Efficiency Below Threshold (Bank 2)',
  // EGR
  'P0400': 'Exhaust Gas Recirculation Flow Malfunction',
  'P0401': 'EGR Flow Insufficient Detected',
  'P0402': 'EGR Flow Excessive Detected',
  'P0403': 'EGR Control Circuit Malfunction',
  'P0404': 'EGR Control Circuit Range/Performance',
  'P0405': 'EGR Sensor A Circuit Low',
  'P0406': 'EGR Sensor A Circuit High',
  'P0407': 'EGR Sensor B Circuit Low',
  'P0408': 'EGR Sensor B Circuit High',
  // DPF
  'P2002': 'DPF Efficiency Below Threshold (Bank 1)',
  'P2003': 'DPF Efficiency Below Threshold (Bank 2)',
  'P244A': 'DPF Differential Pressure Too Low',
  'P244B': 'DPF Differential Pressure Too High',
  'P2452': 'DPF Pressure Sensor A Circuit',
  'P2453': 'DPF Pressure Sensor A Range/Performance',
  'P2454': 'DPF Pressure Sensor A Circuit Low',
  'P2455': 'DPF Pressure Sensor A Circuit High',
  'P2458': 'DPF Regeneration Duration',
  'P2459': 'DPF Regeneration Frequency',
  'P2463': 'DPF Soot Accumulation',
  // SCR/AdBlue
  'P20E8': 'Reductant Pressure Too Low',
  'P20EE': 'SCR NOx Catalyst Efficiency Below Threshold (Bank 1)',
  'P20EF': 'SCR NOx Catalyst Efficiency Below Threshold (Bank 2)',
  'P2200': 'NOx Sensor Circuit (Bank 1)',
  'P2201': 'NOx Sensor Circuit Range/Performance (Bank 1)',
  'P2202': 'NOx Sensor Circuit Low Input (Bank 1)',
  'P2203': 'NOx Sensor Circuit High Input (Bank 1)',
  'P2BAD': 'NOx Exceedance - SCR Efficiency Below Threshold',
  'P2BAE': 'NOx Exceedance - Derating Active',
  'P203B': 'Reductant Level Sensor Range/Performance',
  'P203F': 'Reductant Level Too Low',
  'P207F': 'Reductant Quality Performance',
  'P20A1': 'Reductant Injection Malfunction',
  // O2 Sensors
  'P0130': 'O2 Sensor Circuit Malfunction (Bank 1 Sensor 1)',
  'P0131': 'O2 Sensor Circuit Low Voltage (Bank 1 Sensor 1)',
  'P0132': 'O2 Sensor Circuit High Voltage (Bank 1 Sensor 1)',
  'P0133': 'O2 Sensor Circuit Slow Response (Bank 1 Sensor 1)',
  'P0134': 'O2 Sensor No Activity Detected (Bank 1 Sensor 1)',
  'P0135': 'O2 Sensor Heater Circuit Malfunction (Bank 1 Sensor 1)',
  'P0136': 'O2 Sensor Circuit Malfunction (Bank 1 Sensor 2)',
  'P0137': 'O2 Sensor Circuit Low Voltage (Bank 1 Sensor 2)',
  'P0138': 'O2 Sensor Circuit High Voltage (Bank 1 Sensor 2)',
  'P0139': 'O2 Sensor Circuit Slow Response (Bank 1 Sensor 2)',
  'P0140': 'O2 Sensor No Activity Detected (Bank 1 Sensor 2)',
  'P0141': 'O2 Sensor Heater Circuit Malfunction (Bank 1 Sensor 2)',
  // Turbo
  'P0234': 'Turbocharger Overboost Condition',
  'P0235': 'Turbocharger Boost Sensor A Circuit Malfunction',
  'P0236': 'Turbocharger Boost Sensor A Range/Performance',
  'P0299': 'Turbocharger Underboost Condition',
};

// Get description for a DTC code
const getDTCDescription = (code) => {
  return DTC_DESCRIPTIONS[code?.toUpperCase()] || 'Diagnostic Trouble Code';
};

// Common DTC categories for quick selection
const DTC_CATEGORIES = {
  dpf: {
    name: 'DPF (Diesel Particulate Filter)',
    icon: '🔧',
    codes: ['P2002', 'P2003', 'P242F', 'P244A', 'P244B', 'P2452', 'P2453', 'P2454', 'P2455', 'P2458', 'P2459', 'P2463']
  },
  egr: {
    name: 'EGR (Exhaust Gas Recirculation)',
    icon: '♻️',
    codes: ['P0400', 'P0401', 'P0402', 'P0403', 'P0404', 'P0405', 'P0406', 'P0407', 'P0408', 'P0489', 'P0490', 'P2141', 'P2142', 'P2143', 'P2144', 'P2145']
  },
  adblue: {
    name: 'AdBlue/SCR/DEF',
    icon: '💧',
    codes: ['P20E8', 'P20EE', 'P20EF', 'P2200', 'P2201', 'P2202', 'P2203', 'P2BAD', 'P2BAE', 'P203B', 'P203F', 'P207F', 'P20A1', 'P2047', 'P204F', 'P2048', 'P2049']
  },
  catalyst: {
    name: 'Catalyst/CAT',
    icon: '🌡️',
    codes: ['P0420', 'P0421', 'P0422', 'P0423', 'P0424', 'P0430', 'P0431', 'P0432', 'P0433', 'P0434']
  },
  o2_lambda: {
    name: 'O2/Lambda Sensors',
    icon: '📊',
    codes: ['P0130', 'P0131', 'P0132', 'P0133', 'P0134', 'P0135', 'P0136', 'P0137', 'P0138', 'P0139', 'P0140', 'P0141', 'P0150', 'P0151', 'P0152', 'P0153', 'P0154', 'P0155', 'P0156', 'P0157', 'P0158', 'P0159', 'P0160', 'P0161']
  },
  turbo: {
    name: 'Turbo/Boost',
    icon: '💨',
    codes: ['P0234', 'P0235', 'P0236', 'P0237', 'P0238', 'P0243', 'P0244', 'P0245', 'P0299', 'P0045', 'P0046', 'P0047', 'P0048']
  },
  flaps: {
    name: 'Swirl Flaps/Intake',
    icon: '🌀',
    codes: ['P2004', 'P2005', 'P2006', 'P2007', 'P2008', 'P2009', 'P2014', 'P2015', 'P2016', 'P2017', 'P2070', 'P2071']
  },
  tva: {
    name: 'TVA (Throttle Valve)',
    icon: '🎚️',
    codes: ['P0120', 'P0121', 'P0122', 'P0123', 'P0220', 'P0221', 'P0222', 'P0223', 'P2101', 'P2102', 'P2103', 'P2104', 'P2105', 'P2106', 'P2107', 'P2108', 'P2110', 'P2111', 'P2112', 'P2118', 'P2119']
  },
  nox: {
    name: 'NOx Sensor',
    icon: '☁️',
    codes: ['P2200', 'P2201', 'P2202', 'P2203', 'P2204', 'P2205', 'P2206', 'P2207', 'P2208', 'P2209', 'P229E', 'P229F', 'P22A0', 'P22A1', 'P22A2', 'P22A3']
  },
  readiness: {
    name: 'OBD Readiness',
    icon: '✅',
    codes: ['P1000', 'U0100', 'U0101', 'U0102', 'U0103', 'U0104', 'U0105', 'U0106', 'U0107', 'U0108', 'U0109', 'U0110']
  },
  immo: {
    name: 'Immobilizer (IMMO)',
    icon: '🔐',
    codes: ['P1570', 'P1571', 'P1600', 'P1601', 'P1602', 'P1603', 'P1604', 'P1605', 'P1612', 'P1614', 'P1615', 'P1616', 'P1617']
  },
  speed_limiter: {
    name: 'Speed Limiter (Vmax)',
    icon: '⚡',
    codes: ['P0500', 'P0501', 'P0502', 'P0503', 'P0504', 'P0219', 'P0220', 'P2135']
  },
  start_stop: {
    name: 'Start & Stop System',
    icon: '🔄',
    codes: ['P0560', 'P0562', 'P0563', 'P1A10', 'P1A13', 'P1A14', 'P1A15', 'P1A16', 'P1A17']
  },
  fuel: {
    name: 'Fuel System',
    icon: '⛽',
    codes: ['P0087', 'P0088', 'P0089', 'P0090', 'P0091', 'P0092', 'P0093', 'P0094', 'P0171', 'P0172', 'P0173', 'P0174', 'P0175', 'P0191', 'P0192', 'P0193', 'P0201', 'P0202', 'P0203', 'P0204', 'P0205', 'P0206']
  },
  evap: {
    name: 'EVAP System',
    icon: '💨',
    codes: ['P0440', 'P0441', 'P0442', 'P0443', 'P0444', 'P0445', 'P0446', 'P0447', 'P0448', 'P0449', 'P0450', 'P0451', 'P0452', 'P0453', 'P0455', 'P0456']
  }
};

const DTCDeletePage = () => {
  const navigate = useNavigate();
  
  // File state
  const [file, setFile] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Analysis state
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [showFileDtcSelector, setShowFileDtcSelector] = useState(false);
  
  // DTC Database state (from DaVinci)
  const [dtcDatabase, setDtcDatabase] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // DTC input state
  const [dtcInput, setDtcInput] = useState('');
  const [selectedDTCs, setSelectedDTCs] = useState([]);
  const [correctChecksum, setCorrectChecksum] = useState(true);
  
  // Processing state
  const [processing, setProcessing] = useState(false);
  const [processResult, setProcessResult] = useState(null);
  
  // Payment state
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [orderId, setOrderId] = useState(null);
  
  // Step tracking: 1: Upload, 2: Select DTCs, 3: Payment, 4: Results
  const [step, setStep] = useState(1);

  // Load DTC Database from backend (DaVinci data)
  useEffect(() => {
    const loadDTCDatabase = async () => {
      try {
        const response = await axios.get(`${API}/dtc-database`);
        if (response.data.success) {
          setDtcDatabase(response.data);
          console.log(`Loaded ${response.data.total_codes} DTC codes from database`);
        }
      } catch (error) {
        console.log('Using local DTC database');
      }
    };
    loadDTCDatabase();
  }, []);

  // Search DTC codes
  const searchDTCs = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/dtc-database/search?q=${encodeURIComponent(query)}`);
      if (response.data.success) {
        setSearchResults(response.data.results.slice(0, 10));
      }
    } catch (error) {
      // Fallback to local search
      const results = Object.entries(DTC_DESCRIPTIONS)
        .filter(([code, desc]) => 
          code.toLowerCase().includes(query.toLowerCase()) || 
          desc.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 10)
        .map(([code, description]) => ({ code, description }));
      setSearchResults(results);
    }
  };

  // Debounced search handler
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchDTCs(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Pricing calculator
  const calculatePrice = () => {
    const dtcCount = selectedDTCs.length;
    let dtcPrice = 0;
    
    if (dtcCount === 0) {
      dtcPrice = 0;
    } else if (dtcCount === 1) {
      dtcPrice = 10; // $10 for 1 DTC
    } else if (dtcCount >= 2 && dtcCount <= 6) {
      dtcPrice = 20; // $20 for 2-6 DTCs
    } else {
      dtcPrice = 30; // $30 for 7+ DTCs
    }
    
    const checksumPrice = correctChecksum ? 5 : 0; // $5 for checksum
    
    return {
      dtcPrice,
      checksumPrice,
      total: dtcPrice + checksumPrice
    };
  };

  const pricing = calculatePrice();

  // File upload handler
  const onDrop = useCallback(async (acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    if (!uploadedFile) return;
    
    setFile(uploadedFile);
    setUploading(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append('file', uploadedFile);
    
    try {
      const response = await axios.post(`${API}/dtc-engine/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });
      
      if (response.data.success) {
        setFileId(response.data.file_id);
        setAnalysisResult(response.data.analysis);
        setStep(2);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload file: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/octet-stream': ['.bin', '.ori', '.mod', '.fls', '.hex', '.mpc']
    },
    maxFiles: 1,
    disabled: uploading
  });

  // Add DTC codes from input
  const addDTCsFromInput = () => {
    const codes = dtcInput
      .toUpperCase()
      .split(/[,;\s]+/)
      .map(c => c.trim())
      .filter(c => /^[PCBU][0-9A-Fa-f]{4}$/.test(c))
      .filter(c => !selectedDTCs.includes(c));
    
    if (codes.length > 0) {
      setSelectedDTCs([...selectedDTCs, ...codes]);
      setDtcInput('');
    }
  };

  // Add DTCs from category
  const addCategoryDTCs = (categoryKey) => {
    const category = DTC_CATEGORIES[categoryKey];
    const newCodes = category.codes.filter(c => !selectedDTCs.includes(c));
    setSelectedDTCs([...selectedDTCs, ...newCodes]);
  };

  // Remove DTC from selection
  const removeDTC = (code) => {
    setSelectedDTCs(selectedDTCs.filter(c => c !== code));
  };

  // Clear all selected DTCs
  const clearAllDTCs = () => {
    setSelectedDTCs([]);
  };

  // Process file (delete DTCs)
  const processFile = async () => {
    if (!fileId || selectedDTCs.length === 0) return;
    
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/dtc-engine/process`, {
        file_id: fileId,
        dtc_codes: selectedDTCs,
        correct_checksum: correctChecksum,
        order_id: orderId
      });
      
      setProcessResult(response.data);
      setStep(4); // Changed from 3 to 4 (after payment step)
    } catch (error) {
      console.error('Processing error:', error);
      alert('Failed to process file: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
    }
  };

  // Download modified file
  const downloadFile = async () => {
    if (!processResult?.download_id) return;
    
    try {
      const response = await axios.get(`${API}/dtc-engine/download/${processResult.download_id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${file?.name?.replace(/\.[^.]+$/, '')}_dtc_deleted.bin`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file');
    }
  };

  // Reset for new file
  const resetAll = () => {
    setFile(null);
    setFileId(null);
    setAnalysisResult(null);
    setSelectedDTCs([]);
    setDtcInput('');
    setProcessResult(null);
    setCustomerName('');
    setCustomerEmail('');
    setOrderId(null);
    setStep(1);
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{SEO_DATA.title}</title>
        <meta name="description" content={SEO_DATA.description} />
        <meta name="keywords" content={SEO_DATA.keywords} />
        <link rel="canonical" href={SEO_DATA.canonical} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={SEO_DATA.title} />
        <meta property="og:description" content={SEO_DATA.description} />
        <meta property="og:url" content={SEO_DATA.canonical} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={SEO_DATA.title} />
        <meta name="twitter:description" content={SEO_DATA.description} />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <meta name="googlebot" content="index, follow" />
        <meta name="author" content="ECU Flash Service" />
        
        {/* Structured Data - Product Schema (supports aggregateRating for Rich Results) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "DTC Delete Service",
            "description": "Professional diagnostic trouble code (DTC) removal service for vehicles. Remove check engine lights, clear fault codes permanently from ECU files.",
            "image": "https://ecuflashservice.com/favicon-512.png",
            "brand": {
              "@type": "Brand",
              "name": "ECU Flash Service"
            },
            "offers": {
              "@type": "AggregateOffer",
              "lowPrice": "10",
              "highPrice": "30",
              "priceCurrency": "USD",
              "offerCount": "3",
              "availability": "https://schema.org/InStock"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "bestRating": "5",
              "worstRating": "1",
              "ratingCount": "3500",
              "reviewCount": "3500"
            }
          })}
        </script>
        
        {/* FAQ Schema for common questions */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is DTC delete?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "DTC delete is the process of permanently removing Diagnostic Trouble Codes from your vehicle's ECU file. This prevents check engine lights from appearing for specific fault codes like P0420, P2002, or EGR-related codes."
                }
              },
              {
                "@type": "Question",
                "name": "How much does DTC removal cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our DTC removal service starts at $10 for a single code, $20 for 2-6 codes, and $30 for 7 or more codes. Checksum correction is included at $5 additional."
                }
              },
              {
                "@type": "Question",
                "name": "Is DTC delete permanent?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, when we remove DTCs from your ECU file, the codes are permanently deleted. The check engine light will not return for those specific codes."
                }
              },
              {
                "@type": "Question",
                "name": "What DTC codes can you remove?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We can remove almost any DTC including P0420 (catalyst), P0401-P0408 (EGR), P2002/P2463 (DPF), P20EE/P203F (AdBlue/SCR), O2 sensor codes, turbo codes, and many more."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <StepHeader
        title="DTC Delete Tool"
        subtitle="Remove check engine light permanently"
        currentStep={step}
        icon="🔧"
        backLink="/"
        backLabel="← Back"
        steps={[
          { num: 1, label: 'Upload' },
          { num: 2, label: 'Select' },
          { num: 3, label: 'Pay' },
          { num: 4, label: 'Done' }
        ]}
        stepTitles={{
          1: 'Upload ECU File',
          2: 'Select DTC Codes',
          3: 'Complete Payment',
          4: 'Download Ready'
        }}
        stepSubtitles={{
          1: 'Upload your ECU file to scan and delete specific DTCs',
          2: 'Choose which fault codes to remove permanently',
          3: 'Secure payment via PayPal',
          4: 'Your modified file is ready for download'
        }}
      />

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        
        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Pricing Cards - Enhanced Design */}
            <div className="relative">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* Single DTC */}
                <div className="group relative bg-gradient-to-b from-[#1A1A1A] to-[#0A0A0A] border border-white/10 p-4 hover:border-[#00F0FF]/30 transition-all duration-300">
                  <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#00F0FF]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#00F0FF] mb-1">$10</div>
                    <div className="text-[#888] text-sm font-medium">1 DTC</div>
                    <div className="text-[#666] text-xs mt-1">Single code</div>
                  </div>
                </div>
                
                {/* 2-6 DTCs - Popular */}
                <div className="group relative bg-gradient-to-b from-[#00F0FF]/10 to-[#0A0A0A] border border-[#00F0FF]/30 p-4 hover:border-[#00F0FF]/50 transition-all duration-300">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00F0FF] text-black text-xs font-bold px-2 py-0.5 uppercase tracking-wider">Popular</div>
                  <div className="absolute top-0 left-0 w-full h-0.5 bg-[#00F0FF]/50" />
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#00F0FF] mb-1">$20</div>
                    <div className="text-[#888] text-sm font-medium">2-6 DTCs</div>
                    <div className="text-[#666] text-xs mt-1">Best value</div>
                  </div>
                </div>
                
                {/* 7+ DTCs */}
                <div className="group relative bg-gradient-to-b from-[#1A1A1A] to-[#0A0A0A] border border-white/10 p-4 hover:border-[#00F0FF]/30 transition-all duration-300">
                  <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#00F0FF]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#00F0FF] mb-1">$30</div>
                    <div className="text-[#888] text-sm font-medium">7+ DTCs</div>
                    <div className="text-[#666] text-xs mt-1">Unlimited</div>
                  </div>
                </div>
                
                {/* Checksum */}
                <div className="group relative bg-gradient-to-b from-[#FF003C]/5 to-[#0A0A0A] border border-[#FF003C]/20 p-4 hover:border-[#FF003C]/40 transition-all duration-300">
                  <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#FF003C]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#FF003C] mb-1">+$5</div>
                    <div className="text-[#888] text-sm font-medium">Checksum</div>
                    <div className="text-[#666] text-xs mt-1">Auto-fix</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Banner - Enhanced */}
            <div className="relative overflow-hidden bg-gradient-to-r from-[#00F0FF]/5 via-[#00F0FF]/10 to-[#00F0FF]/5 border border-[#00F0FF]/20 p-4">
              <div className="absolute top-0 left-0 w-1 h-full bg-[#00F0FF]" />
              <div className="flex items-start gap-3 pl-2">
                <div className="w-8 h-8 bg-[#00F0FF]/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-[#00F0FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-[#00F0FF] font-medium text-sm">Multi-Instance Removal</p>
                  <p className="text-[#888] text-xs mt-1">Each DTC (e.g., P0421) may have multiple sub-codes. We automatically find and remove all instances.</p>
                </div>
              </div>
            </div>
            
            {/* Upload Zone - Enhanced */}
            <div
              {...getRootProps()}
              data-testid="dtc-upload-dropzone"
              className={`relative overflow-hidden transition-all duration-300 cursor-pointer group ${
                isDragActive
                  ? 'border-[#00F0FF] bg-[#00F0FF]/10'
                  : 'border-white/20 hover:border-[#00F0FF]/50'
              }`}
            >
              {/* Background Pattern */}
              <div className="absolute inset-0 grid-bg opacity-5" />
              
              {/* Border with gradient effect */}
              <div className="relative border-2 border-dashed border-inherit p-12">
                <input {...getInputProps()} />
                
                {uploading ? (
                  <div className="text-center">
                    <div className="relative w-20 h-20 mx-auto mb-6">
                      {/* Spinning ring */}
                      <div className="absolute inset-0 border-4 border-[#00F0FF]/20 rounded-full" />
                      <div className="absolute inset-0 border-4 border-transparent border-t-[#00F0FF] rounded-full animate-spin" />
                      {/* Progress text */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-[#00F0FF] font-bold text-lg">{uploadProgress}%</span>
                      </div>
                    </div>
                    <p className="text-white font-medium mb-2">Uploading your file...</p>
                    <div className="w-64 mx-auto bg-[#1A1A1A] h-1.5 overflow-hidden">
                      <div 
                        className="bg-gradient-to-r from-[#00F0FF] to-[#00F0FF]/50 h-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }} 
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    {/* Upload Icon */}
                    <div className="relative w-20 h-20 mx-auto mb-6">
                      <div className="absolute inset-0 bg-gradient-to-br from-[#00F0FF]/20 to-transparent border border-[#00F0FF]/30 flex items-center justify-center group-hover:border-[#00F0FF]/50 transition-all">
                        <svg className="w-10 h-10 text-[#00F0FF] group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      </div>
                      {/* Glow effect on hover */}
                      <div className="absolute inset-0 bg-[#00F0FF]/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity -z-10" />
                    </div>
                    
                    <p className="text-white font-heading text-xl uppercase tracking-wide mb-2">
                      {isDragActive ? 'Drop your file here' : 'Drag & drop your ECU file'}
                    </p>
                    <p className="text-[#666] text-sm mb-4">or click anywhere to browse</p>
                    
                    {/* Supported formats */}
                    <div className="flex justify-center gap-2 flex-wrap">
                      {['.bin', '.ori', '.mod', '.fls', '.hex', '.mpc'].map(ext => (
                        <span key={ext} className="bg-[#1A1A1A] border border-white/10 px-2 py-1 text-xs text-[#888] font-mono">
                          {ext}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Trust Badges */}
            <div className="flex justify-center gap-6 text-center">
              <div className="flex items-center gap-2 text-[#888] text-sm">
                <svg className="w-4 h-4 text-[#00F0FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>Secure Processing</span>
              </div>
              <div className="flex items-center gap-2 text-[#888] text-sm">
                <svg className="w-4 h-4 text-[#00F0FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span>Instant Results</span>
              </div>
              <div className="flex items-center gap-2 text-[#888] text-sm">
                <svg className="w-4 h-4 text-[#00F0FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>3500+ Completed</span>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Select DTCs */}
        {step === 2 && analysisResult && (
          <div className="space-y-6">
            {/* File Info Card */}
            <div className="bg-[#0A0A0A] border border-white/10 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-500/100/20 flex items-center justify-center">
                    <span className="text-2xl">✅</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{file?.name}</h3>
                    <p className="text-sm text-[#888]">
                      {(file?.size / 1024 / 1024).toFixed(2)} MB
                      {analysisResult.ecu_info?.manufacturer && analysisResult.ecu_info.manufacturer !== 'Unknown' && ` • ${analysisResult.ecu_info.manufacturer}`}
                      {analysisResult.ecu_info?.type && analysisResult.ecu_info.type !== 'Unknown' && ` • ${analysisResult.ecu_info.type}`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={resetAll}
                  className="text-[#888] hover:text-[#00F0FF] transition"
                >
                  ✕ Change File
                </button>
              </div>
              
              {/* Checksum Info */}
              <div className="bg-[#1A1A1A] p-4 text-sm">
                <div className="flex items-center space-x-2 text-[#888]">
                  <span>🔐</span>
                  <span>Checksum Type: <strong className="text-white">{analysisResult.checksum_info?.type || 'Detecting...'}</strong></span>
                </div>
              </div>
            </div>

            {/* DTCs Found in File - Compact View with View & Select */}
            {analysisResult.detected_dtcs?.length > 0 && (
              <div className="bg-[#0A0A0A] border border-white/10 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#00F0FF]/20 flex items-center justify-center">
                      <span className="text-2xl">🔍</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-3xl font-bold text-[#00F0FF]">{analysisResult.detected_dtcs.length}</span>
                        <span className="text-[#888] font-medium">DTCs Found in File</span>
                      </div>
                      <p className="text-sm text-[#666]">Click to view and select codes for deletion</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowFileDtcSelector(!showFileDtcSelector)}
                    className="flex items-center gap-2 bg-[#00F0FF] hover:bg-[#0A0A0A] text-black px-5 py-3 font-bold uppercase tracking-wider transition"
                  >
                    <span>{showFileDtcSelector ? 'Hide DTCs' : 'View & Select'}</span>
                    <svg className={`w-5 h-5 transition-transform ${showFileDtcSelector ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
                
                {/* Expandable DTC Grid */}
                {showFileDtcSelector && (
                  <div className="mt-6 border-t border-white/10 pt-6">
                    {/* Quick Actions */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-[#888]">Select DTCs to add for deletion:</span>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            const newDtcs = analysisResult.detected_dtcs
                              .map(d => d.code)
                              .filter(code => !selectedDTCs.includes(code));
                            setSelectedDTCs([...selectedDTCs, ...newDtcs]);
                          }}
                          className="text-sm text-[#00F0FF] hover:text-white font-medium flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Select All
                        </button>
                        <span className="text-[#666]">|</span>
                        <button
                          type="button"
                          onClick={() => {
                            const fileDtcCodes = analysisResult.detected_dtcs.map(d => d.code);
                            setSelectedDTCs(selectedDTCs.filter(code => !fileDtcCodes.includes(code)));
                          }}
                          className="text-sm text-[#888] hover:text-white font-medium flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Clear File DTCs
                        </button>
                      </div>
                    </div>
                    
                    {/* DTC Grid with Checkboxes */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto p-1">
                      {analysisResult.detected_dtcs.map((dtc, idx) => (
                        <label
                          key={idx}
                          className={`flex items-center gap-2 p-2 border cursor-pointer transition ${
                            selectedDTCs.includes(dtc.code)
                              ? 'bg-[#00F0FF]/10 border-[#00F0FF]'
                              : 'bg-[#1A1A1A] border-white/10 hover:border-[#00F0FF]/30 hover:bg-[#0A0A0A]'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedDTCs.includes(dtc.code)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDTCs([...selectedDTCs, dtc.code]);
                              } else {
                                setSelectedDTCs(selectedDTCs.filter(c => c !== dtc.code));
                              }
                            }}
                            className="h-4 w-4 text-[#00F0FF] border-white/20 bg-[#1A1A1A] focus:ring-[#00F0FF]"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="font-mono font-semibold text-sm text-white">{dtc.code}</span>
                            {dtc.description && (
                              <p className="text-xs text-[#666] truncate" title={dtc.description}>{dtc.description}</p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                    
                    {/* Selection Summary */}
                    <div className="mt-4 flex items-center justify-between p-3 bg-[#00F0FF]/10 border border-[#00F0FF]/20">
                      <span className="text-sm text-[#00F0FF]">
                        <strong>{selectedDTCs.filter(code => analysisResult.detected_dtcs.map(d => d.code).includes(code)).length}</strong> of {analysisResult.detected_dtcs.length} file DTCs selected
                      </span>
                      <span className="text-sm text-[#00F0FF]">
                        Total selected: <strong>{selectedDTCs.length}</strong>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* DTC Input Section */}
            <div className="bg-[#0A0A0A] border border-white/10 p-6">
              <h3 className="font-semibold text-white mb-4">Select DTCs to Delete</h3>
              
              {/* Search Input with Autocomplete */}
              <div className="mb-6 relative">
                <label className="block text-sm font-medium text-[#888] mb-2">🔍 Search DTC Codes</label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by code or description (e.g., P0420, DPF, catalyst)..."
                  className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]/50 focus:outline-none"
                />
                
                {/* Autocomplete Results */}
                {searchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-[#0A0A0A] border border-white/10 shadow-lg max-h-64 overflow-y-auto">
                    {searchResults.map((result) => (
                      <button
                        key={result.code}
                        onClick={() => {
                          if (!selectedDTCs.includes(result.code)) {
                            setSelectedDTCs([...selectedDTCs, result.code]);
                          }
                          setSearchQuery('');
                          setSearchResults([]);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-[#1A1A1A] border-b border-white/5 last:border-b-0 flex items-center justify-between"
                      >
                        <div>
                          <span className="font-mono font-semibold text-[#00F0FF]">{result.code}</span>
                          <span className="text-[#888] text-sm ml-2">{result.description}</span>
                        </div>
                        {selectedDTCs.includes(result.code) ? (
                          <span className="text-green-400 text-sm">✓ Added</span>
                        ) : (
                          <span className="text-[#666] text-sm">+ Add</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Manual Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#888] mb-2">Enter DTC Codes Manually</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={dtcInput}
                    onChange={(e) => setDtcInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addDTCsFromInput()}
                    placeholder="P0420, P2002, P0401..."
                    className="flex-1 bg-[#1A1A1A] border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]/50 focus:outline-none"
                  />
                  <button
                    onClick={addDTCsFromInput}
                    className="bg-[#00F0FF] hover:bg-[#0A0A0A] text-black px-6 py-3 font-bold uppercase tracking-wider transition"
                  >
                    Add
                  </button>
                </div>
                <p className="text-xs text-[#666] mt-1">Enter one or more DTC codes separated by commas or spaces</p>
              </div>

              {/* Category Quick Select - Enhanced with counts */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#888] mb-2">Quick Add by Category</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(DTC_CATEGORIES).map(([key, cat]) => {
                    // Use database counts if available
                    const dbCodes = dtcDatabase?.categories?.[key] || cat.codes;
                    return (
                      <button
                        key={key}
                        onClick={() => addCategoryDTCs(key)}
                        className="flex items-center justify-between bg-[#1A1A1A] hover:bg-[#00F0FF]/10 border border-white/10 hover:border-blue-300  px-4 py-3 text-left transition"
                      >
                        <div className="flex items-center space-x-2">
                          <span>{cat.icon}</span>
                          <span className="text-sm font-medium text-[#888]">{cat.name}</span>
                        </div>
                        <span className="text-xs bg-gray-200 text-[#888] px-2 py-0.5 ">
                          {dbCodes.length}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Selected DTCs */}
              {selectedDTCs.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-[#888]">
                      Selected DTCs ({selectedDTCs.length})
                    </label>
                    <button
                      onClick={clearAllDTCs}
                      className="text-xs text-red-500 hover:text-red-600"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="bg-[#1A1A1A]  p-4">
                    <div className="flex flex-wrap gap-2">
                      {selectedDTCs.map((code) => (
                        <span
                          key={code}
                          className="inline-flex items-center bg-[#00F0FF]/20 text-[#00F0FF] px-3 py-1.5  text-sm font-mono group relative"
                          title={getDTCDescription(code)}
                        >
                          {code}
                          <button
                            onClick={() => removeDTC(code)}
                            className="ml-2 text-blue-500 hover:text-red-500"
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                    {/* Show descriptions for selected DTCs */}
                    <div className="mt-3 border-t border-white/10 pt-3">
                      <p className="text-xs text-[#666] mb-2">Selected Code Descriptions:</p>
                      <div className="text-xs text-[#888] space-y-1">
                        {selectedDTCs.slice(0, 5).map(code => (
                          <div key={code}>
                            <span className="font-mono font-semibold">{code}</span>: {getDTCDescription(code)}
                          </div>
                        ))}
                        {selectedDTCs.length > 5 && (
                          <div className="text-[#666]">...and {selectedDTCs.length - 5} more</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Checksum Option */}
              <div className="flex items-center space-x-3 mb-6 p-4 bg-[#00F0FF]/10 ">
                <input
                  type="checkbox"
                  id="checksum"
                  checked={correctChecksum}
                  onChange={(e) => setCorrectChecksum(e.target.checked)}
                  className="w-5 h-5 text-[#00F0FF] rounded"
                />
                <label htmlFor="checksum" className="text-sm flex-1">
                  <span className="font-medium text-white">Automatic Checksum Correction (+$5)</span>
                  <span className="text-[#666] block">Recalculate file checksum after DTC deletion</span>
                </label>
                <span className="text-[#00F0FF] font-semibold">${correctChecksum ? '5.00' : '0.00'}</span>
              </div>

              {/* Price Summary */}
              {selectedDTCs.length > 0 && (
                <div className="bg-gradient-to-r from-gray-50 to-gray-100  p-4 mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[#888]">DTC Removal ({selectedDTCs.length} code{selectedDTCs.length !== 1 ? 's' : ''})</span>
                    <span className="font-semibold">${pricing.dtcPrice.toFixed(2)}</span>
                  </div>
                  {correctChecksum && (
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-[#888]">Checksum Correction</span>
                      <span className="font-semibold">${pricing.checksumPrice.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-white/10 pt-2 mt-2 flex justify-between items-center">
                    <span className="font-bold text-white">Total</span>
                    <span className="text-2xl font-bold text-[#00F0FF]">${pricing.total.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Continue to Payment Button */}
              <button
                onClick={() => {
                  setStep(3);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                disabled={selectedDTCs.length === 0}
                className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white py-4  font-semibold text-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                <span>💳</span>
                <span>Continue to Payment - ${pricing.total.toFixed(2)}</span>
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Payment */}
        {step === 3 && (
          <div className="space-y-6">
            {/* Order Summary */}
            <div className="bg-[#0A0A0A] border border-white/10  p-6">
              <h3 className="font-semibold text-white mb-4 flex items-center">
                <span className="mr-2">📋</span> Order Summary
              </h3>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-[#888]">File</span>
                  <span className="font-medium text-white">{file?.name}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-[#888]">DTCs to Delete</span>
                  <span className="font-medium text-white">{selectedDTCs.length} code{selectedDTCs.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex flex-wrap gap-1 py-2 border-b border-gray-100">
                  {selectedDTCs.map(code => (
                    <span key={code} className="bg-[#00F0FF]/20 text-[#00F0FF] px-2 py-0.5 rounded text-xs font-mono">{code}</span>
                  ))}
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-[#888]">DTC Removal</span>
                  <span className="font-semibold">${pricing.dtcPrice.toFixed(2)}</span>
                </div>
                {correctChecksum && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-[#888]">Checksum Correction</span>
                    <span className="font-semibold">${pricing.checksumPrice.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-3 bg-[#1A1A1A]  px-3">
                  <span className="font-bold text-white">Total</span>
                  <span className="text-2xl font-bold text-[#00F0FF]">${pricing.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Customer Info */}
            <div className="bg-[#0A0A0A] border border-white/10  p-6">
              <h3 className="font-semibold text-white mb-4 flex items-center">
                <span className="mr-2">👤</span> Your Information
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#888] mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full bg-[#1A1A1A] border border-white/10  px-4 py-3 text-white focus:border-blue-500 focus:ring-1 focus:ring-red-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#888] mb-2">Email Address *</label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full bg-[#1A1A1A] border border-white/10  px-4 py-3 text-white focus:border-blue-500 focus:ring-1 focus:ring-red-500"
                    required
                  />
                  <p className="text-xs text-[#666] mt-1">We will send the modified file to this email</p>
                </div>
              </div>
            </div>

            {/* Payment Section */}
            <div className="bg-[#0A0A0A] border border-white/10  p-6">
              <h3 className="font-semibold text-white mb-4 flex items-center">
                <span className="mr-2">💳</span> Payment
              </h3>
              
              {/* PayPal Integration */}
              <div className="bg-[#1A1A1A]  p-6 mb-4">
                {pricing.total > 0 ? (
                  <PayPalScriptProvider 
                    options={{ 
                      "client-id": PAYPAL_CLIENT_ID, 
                      currency: "USD",
                      intent: "capture",
                      "disable-funding": "paylater"
                    }}
                  >
                    <PayPalButtons
                      style={{ 
                        layout: "vertical",
                        color: "blue",
                        shape: "rect",
                        label: "pay"
                      }}
                      disabled={!customerName || !customerEmail || processing}
                      createOrder={(data, actions) => {
                        console.log("Creating PayPal order for:", pricing.total.toFixed(2), "USD");
                        return actions.order.create({
                          purchase_units: [{
                            amount: {
                              value: pricing.total.toFixed(2),
                              currency_code: "USD"
                            },
                            description: `DTC Delete Service - ${selectedDTCs.length} code(s)${correctChecksum ? ' + Checksum' : ''}`
                          }]
                        });
                      }}
                      onApprove={async (data, actions) => {
                        console.log("PayPal onApprove called, orderID:", data.orderID);
                        
                        try {
                          setProcessing(true);
                          
                          // Capture the payment
                          let order;
                          try {
                            console.log("Attempting to capture payment...");
                            order = await actions.order.capture();
                            console.log("Payment captured successfully:", order);
                          } catch (captureError) {
                            console.error('PayPal capture error:', captureError);
                            alert(`Payment failed: ${captureError.message || 'Please try again or use a different payment method.'}`);
                            setProcessing(false);
                            return;
                          }
                          
                          // Create order in backend
                          console.log("Creating order in backend...");
                          const orderResponse = await axios.post(`${API}/dtc-engine/order`, {
                            file_id: fileId,
                            dtc_codes: selectedDTCs,
                            correct_checksum: correctChecksum,
                            customer_name: customerName,
                            customer_email: customerEmail,
                            dtc_price: pricing.dtcPrice,
                            checksum_price: pricing.checksumPrice,
                            total_price: pricing.total,
                            paypal_order_id: order.id,
                            paypal_transaction_id: order.purchase_units[0].payments.captures[0].id
                          });
                          console.log("Order created:", orderResponse.data);
                          
                          // Process the DTC deletion
                          console.log("Processing DTC deletion...");
                          const processResponse = await axios.post(`${API}/dtc-engine/process`, {
                            file_id: fileId,
                            dtc_codes: selectedDTCs,
                            correct_checksum: correctChecksum,
                            order_id: orderResponse.data.order_id
                          });
                          console.log("Processing complete:", processResponse.data);
                          
                          setProcessResult(processResponse.data);
                          setStep(4);
                        } catch (error) {
                          console.error('Payment/Order error:', error);
                          console.error('Error response:', error.response);
                          const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
                          alert(`Error processing order: ${errorMsg}\n\nYour payment was successful. Please contact support with PayPal Order ID: ${data.orderID}`);
                        } finally {
                          setProcessing(false);
                        }
                      }}
                      onError={(err) => {
                        console.error('PayPal Error:', err);
                        alert('PayPal payment failed. Please try again.');
                      }}
                    />
                  </PayPalScriptProvider>
                ) : (
                  <p className="text-center text-[#666]">Please select at least one DTC code</p>
                )}
                
                <div className="mt-4 text-center">
                  <p className="text-xs text-[#666]">Secure payment processed by PayPal</p>
                  <p className="text-xs text-[#666] mt-1">Total: ${pricing.total.toFixed(2)} USD</p>
                </div>
              </div>

              <button
                onClick={() => setStep(2)}
                className="w-full py-3 text-[#666] hover:text-[#888] transition"
              >
                ← Back to DTC Selection
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Results */}
        {step === 4 && processResult && (
          <div className="space-y-4">
            {/* Success Banner - Compact */}
            <div className={` p-4 ${processResult.success ? 'bg-green-500/100' : 'bg-yellow-500/100'} text-white`}>
              <div className="flex items-center space-x-3">
                <div className="text-3xl">{processResult.success ? '✅' : '⚠️'}</div>
                <div>
                  <h2 className="text-xl font-bold">
                    {processResult.success ? 'DTC Deletion Complete!' : 'Processing Completed'}
                  </h2>
                  <p className="text-sm opacity-90">
                    {processResult.dtcs_deleted?.length || 0} instance(s) deleted • 
                    {processResult.checksum_corrected ? ' Checksum corrected' : ' Checksum unchanged'}
                  </p>
                </div>
              </div>
            </div>

            {/* ECU Analysis Summary + Results - Combined Card */}
            <div className="bg-[#0A0A0A] border border-white/10  overflow-hidden">
              {/* ECU Analysis Header */}
              {analysisResult && (
                <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-4 py-3">
                  <div className="flex items-center justify-between text-white">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">🔍</span>
                      <div>
                        <div className="font-semibold">
                          {[
                            analysisResult.ecu_info?.manufacturer && analysisResult.ecu_info.manufacturer !== 'Unknown' ? analysisResult.ecu_info.manufacturer : null,
                            analysisResult.ecu_info?.type && analysisResult.ecu_info.type !== 'Unknown' ? analysisResult.ecu_info.type : null
                          ].filter(Boolean).join(' ') || 'ECU File'}
                        </div>
                        <div className="text-xs text-white/80">{file?.name} • {(file?.size / 1024 / 1024).toFixed(2)} MB</div>
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <div className="text-white/80">Checksum</div>
                      <div className="font-mono">{analysisResult.checksum_info?.type || processResult.checksum_type || 'auto'}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Stats Row */}
              <div className="grid grid-cols-3 divide-x divide-gray-200 border-b border-white/10">
                <div className="p-3 text-center">
                  <div className="text-2xl font-bold text-green-400">{processResult.dtcs_deleted?.length || 0}</div>
                  <div className="text-xs text-[#666]">Deleted</div>
                </div>
                <div className="p-3 text-center">
                  <div className="text-2xl font-bold text-yellow-600">{processResult.dtcs_not_found?.length || 0}</div>
                  <div className="text-xs text-[#666]">Not Found</div>
                </div>
                <div className="p-3 text-center">
                  <div className="text-2xl font-bold text-[#00F0FF]">{processResult.checksum_corrected ? '✓' : '—'}</div>
                  <div className="text-xs text-[#666]">Checksum</div>
                </div>
              </div>

              {/* Deleted DTCs - With Descriptions */}
              {processResult.dtcs_deleted?.length > 0 && (
                <div className="p-4 border-b border-white/10">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-green-500">✓</span>
                    <span className="text-sm font-medium text-[#888]">Successfully Deleted ({processResult.dtcs_deleted.length})</span>
                  </div>
                  <div className="space-y-2">
                    {/* Group by DTC code and show with description */}
                    {Object.entries(
                      processResult.dtcs_deleted.reduce((acc, dtc) => {
                        if (!acc[dtc.code]) {
                          acc[dtc.code] = { count: 0, description: dtc.description };
                        }
                        acc[dtc.code].count += 1;
                        return acc;
                      }, {})
                    ).map(([code, data]) => (
                      <div key={code} className="flex items-start gap-2 bg-green-500/10  p-2">
                        <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded font-mono text-xs font-semibold whitespace-nowrap">
                          {code}
                          {data.count > 1 && <span className="ml-1 bg-green-200 px-1 rounded text-green-800">×{data.count}</span>}
                        </span>
                        <span className="text-xs text-[#888] leading-relaxed">
                          {data.description || 'Diagnostic Trouble Code'}
                        </span>
                      </div>
                    ))}
                  </div>
                  {/* Sub-code note */}
                  {processResult.dtcs_deleted.some(d => d.count > 1 || processResult.dtcs_deleted.filter(x => x.code === d.code).length > 1) && (
                    <p className="text-xs text-[#666] mt-2">
                      💡 Multiple instances (×N) indicate sub-codes/fault bytes were found and deleted.
                    </p>
                  )}
                </div>
              )}

              {/* Not Found DTCs - With Descriptions */}
              {processResult.dtcs_not_found?.length > 0 && (
                <div className="p-4 border-b border-white/10 bg-yellow-500/10/50">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-yellow-500">⚠</span>
                    <span className="text-sm font-medium text-[#888]">Not Found ({processResult.dtcs_not_found.length})</span>
                  </div>
                  <div className="space-y-2">
                    {processResult.dtcs_not_found.map((code, idx) => (
                      <div key={idx} className="flex items-start gap-2 bg-yellow-100/50  p-2">
                        <span className="bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded font-mono text-xs font-semibold whitespace-nowrap">
                          {code}
                        </span>
                        <span className="text-xs text-[#888] leading-relaxed">
                          {getDTCDescription(code)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-yellow-600 mt-2">
                    These DTCs were not found in the file. They may not be stored in this ECU.
                  </p>
                </div>
              )}

              {/* Download Section */}
              {processResult.success && (
                <div className="p-4 bg-[#1A1A1A]">
                  <button
                    onClick={downloadFile}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white py-3  font-semibold transition flex items-center justify-center gap-2"
                  >
                    <span>⬇️</span>
                    <span>Download Modified File</span>
                  </button>
                  <p className="text-center text-xs text-[#666] mt-2">
                    {file?.name?.replace(/\.[^.]+$/, '')}_dtc_deleted.bin
                  </p>
                </div>
              )}
            </div>

            {/* Actions - Compact */}
            <div className="flex gap-3">
              <button
                onClick={() => setStep(2)}
                className="flex-1 bg-[#1A1A1A] hover:bg-gray-200 text-[#888] py-2.5  font-medium text-sm transition"
              >
                ← Edit Selection
              </button>
              <button
                onClick={resetAll}
                className="flex-1 bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 text-[#00F0FF] py-2.5  font-medium text-sm transition"
              >
                Process Another File
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* SEO Content Section - Compact */}
      <section className="bg-[#1A1A1A] py-10 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-xl font-bold text-white mb-4 text-center">
            Professional DTC Delete Service
          </h2>
          
          <p className="text-[#888] text-sm text-center mb-6">
            Remove check engine lights and diagnostic trouble codes permanently from your ECU file. 
            DPF, EGR, AdBlue, Catalyst codes - starting from $10.
          </p>
          
          {/* Common DTC Codes - Compact Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-[#0A0A0A]  p-3 border border-white/10">
              <h4 className="font-semibold text-white text-sm mb-1">🔧 DPF</h4>
              <p className="text-xs text-[#666]">P2002, P2463, P244A</p>
            </div>
            <div className="bg-[#0A0A0A]  p-3 border border-white/10">
              <h4 className="font-semibold text-white text-sm mb-1">♻️ EGR</h4>
              <p className="text-xs text-[#666]">P0400-P0408</p>
            </div>
            <div className="bg-[#0A0A0A]  p-3 border border-white/10">
              <h4 className="font-semibold text-white text-sm mb-1">💧 AdBlue</h4>
              <p className="text-xs text-[#666]">P20EE, P203F, P207F</p>
            </div>
            <div className="bg-[#0A0A0A]  p-3 border border-white/10">
              <h4 className="font-semibold text-white text-sm mb-1">🌡️ Catalyst</h4>
              <p className="text-xs text-[#666]">P0420, P0430</p>
            </div>
          </div>
          
          {/* Supported Vehicles */}
          <div className="bg-[#00F0FF]/10 border border-blue-100  p-5 mb-6">
            <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
              <span className="mr-2">🚗</span> Supported Vehicles
            </h4>
            <p className="text-[#00F0FF] text-sm">
              BMW, Mercedes-Benz, Audi, Volkswagen, Ford, Toyota, Hilux, Land Cruiser, Ranger, Triton, 
              Amarok, Land Rover, Isuzu, Nissan, Mazda, Hyundai, Kia, Volvo, Scania, MAN, DAF, Iveco, 
              John Deere, Case, New Holland, Caterpillar, and many more.
            </p>
          </div>
          
          {/* Why Choose Us - Inline */}
          <div className="bg-[#0A0A0A] border border-white/10  p-4 text-sm">
            <div className="flex flex-wrap justify-center gap-4 text-[#888]">
              <span>✓ Permanent removal</span>
              <span>✓ Checksum correction</span>
              <span>✓ Fast turnaround</span>
              <span>✓ All ECU brands</span>
            </div>
          </div>
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default DTCDeletePage;
